import { z } from "zod";
import {
  PAYMENT_CONFIRMATION_MODE_KEY,
  EXPENSE_DECISION_MODE_KEY,
  EXPENSE_DECISION_THRESHOLD_KEY,
  REMINDER_DAYS_BEFORE_KEY,
  PAY_ON_BEHALF_CREATES_DEBT_KEY,
} from "./funds.js";

/**
 * Shared DTO — Admin group-config ESCALATION-EDIT (US-AE-IMP.F4-0115 · @UC9145).
 * Một nguồn sự thật cho api (S02 — PATCH /admin/groups/:id/settings) + admin web (S03 — dialog),
 * tránh drift shape ở 2 nơi (DRY). SUPER_ADMIN sửa 1 key group-config có LÝ DO + audit.
 *
 * WHITELIST (PO chốt 06/07/2026 — index §6): CHỈ 5 key được escalate-edit. Mỗi key:
 *   (a) đã tồn tại trong tập config nhóm, (b) có domain validator rõ ràng
 *   (money-policy `validateMoneyPolicyValue` / reminder range), (c) KHÔNG đụng ledger / tiền đã post.
 * Key ngoài whitelist → schema reject (lớp 1); server S02 enforce LẠI (không tin client).
 *
 * ⚠️ `pay_on_behalf_creates_debt` chạm debt-engine → S02 WIRE consumer THẬT
 * (`resolvePayOnBehalfCreatesDebt` group-tier) để toggle CÓ HIỆU LỰC THẬT — KHÔNG no-op
 * (bài học config-toggle no-op PR#172).
 *
 * BẢO MẬT (invariants):
 *  - Request `.strict()` — chỉ nhận `{key, value, reason}`, chặn field lạ (chống lẫn PII/secret).
 *  - `key` giới hạn bằng enum whitelist — KHÔNG free-string (không mở đường sửa key ledger).
 *  - `reason` bắt buộc (trim, ≥ min) — nguồn 422 ở S02. reason lưu AUDIT-ONLY (không cột riêng).
 *  - Response chỉ {groupId, key, value} — KHÔNG PII / số dư thô.
 */

/** Độ dài lý do escalation: đủ ý nghĩa nghiệp vụ (≥8), chặn phình (≤500 — mirror suspend-reason). */
export const ADMIN_GROUP_CONFIG_REASON_MIN_LENGTH = 8;
export const ADMIN_GROUP_CONFIG_REASON_MAX_LENGTH = 500;

/**
 * Whitelist key group-config được SUPER_ADMIN escalate-edit (PO chốt). Import key constants từ
 * `funds.ts` (KHÔNG hardcode lại literal) — một nguồn tên khoá khớp SystemSetting seed + resolver.
 * LOẠI TRỪ mọi key currency/ledger/số-dư-đã-post (AC3 invariant).
 */
export const ADMIN_GROUP_CONFIG_EDIT_KEYS = [
  PAYMENT_CONFIRMATION_MODE_KEY,
  EXPENSE_DECISION_MODE_KEY,
  EXPENSE_DECISION_THRESHOLD_KEY,
  REMINDER_DAYS_BEFORE_KEY,
  PAY_ON_BEHALF_CREATES_DEBT_KEY,
] as const;

export const adminGroupConfigEditKeySchema = z.enum(ADMIN_GROUP_CONFIG_EDIT_KEYS);

export type AdminGroupConfigEditKey = z.infer<typeof adminGroupConfigEditKeySchema>;

/** Type-guard: `key` ∈ whitelist escalation-edit (server S02 dùng để fail-closed). */
export function isAdminGroupConfigEditKey(key: string): key is AdminGroupConfigEditKey {
  return (ADMIN_GROUP_CONFIG_EDIT_KEYS as readonly string[]).includes(key);
}

/**
 * Value scalar (string | number | boolean) — đủ cho 5 key whitelist (enum mode / ratio number /
 * ngày số / boolean). Validate CHI TIẾT theo domain (enum/range) là việc của S02 backend
 * (`validateMoneyPolicyValue` / reminder range) — contract chỉ ràng buộc shape scalar.
 */
export const adminGroupConfigEditValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);

/** Body PATCH /admin/groups/:id/settings — `{key ∈ whitelist, value scalar, reason ≥ min}`. `.strict()`. */
export const adminGroupConfigEditRequestSchema = z
  .object({
    key: adminGroupConfigEditKeySchema,
    value: adminGroupConfigEditValueSchema,
    reason: z
      .string()
      .trim()
      .min(ADMIN_GROUP_CONFIG_REASON_MIN_LENGTH)
      .max(ADMIN_GROUP_CONFIG_REASON_MAX_LENGTH),
  })
  .strict();

export type AdminGroupConfigEditRequest = z.infer<typeof adminGroupConfigEditRequestSchema>;

/**
 * Response: key + value đã lưu (redacted-safe — chỉ config scalar, KHÔNG PII/số dư thô). `.strict()`.
 */
export const adminGroupConfigEditResponseSchema = z
  .object({
    groupId: z.string(),
    key: adminGroupConfigEditKeySchema,
    value: adminGroupConfigEditValueSchema,
  })
  .strict();

export type AdminGroupConfigEditResponse = z.infer<typeof adminGroupConfigEditResponseSchema>;

/**
 * Code 422 khi `key` không thuộc whitelist (server enforce). Dùng chung api ↔ UI cho message nhất quán.
 */
export const GROUP_CONFIG_KEY_NOT_ALLOWED_CODE = "GROUP_CONFIG_KEY_NOT_ALLOWED" as const;

/** Code 422 khi thiếu/rỗng `reason` (server enforce lại — contract là lớp 1). */
export const GROUP_CONFIG_REASON_REQUIRED_CODE = "GROUP_CONFIG_REASON_REQUIRED" as const;
